import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { TextInput, PasswordInput, Select } from "@mantine/core";
import { Hash } from "lucide-react";
import AuthScreenShell from "../../components/AuthScreenShell";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest } from "../../lib/usePerformRequest";
import { imgNbaLogo } from "../../assets";
import { ButtonLoader } from "../../components/Loader";

import "./styles.scss";
interface SignupResponse {
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
    is_staff: boolean;
    is_lawyer: boolean;
    date_joined: string;
  };
}

interface SignupError {
  email?: string[];
  password?: string[];
  password2?: string[];
  first_name?: string[];
  last_name?: string[];
  [key: string]: any;
}

export default function Signup() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  type FormValues = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    password2: string;
    branch: string;
    scn: string;
  };

  const form = useForm<FormValues>({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      password2: "",
      branch: "",
      scn: "",
    },
    validate: {
      firstName: (value: string) => {
        if (!value.trim()) {
          return "First name is required";
        }
        return null;
      },
      lastName: (value: string) => {
        if (!value.trim()) {
          return "Last name is required";
        }
        return null;
      },
      email: (value: string) => {
        if (!value.trim()) {
          return "Email is required";
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          return "Please enter a valid email address";
        }
        return null;
      },
      password: (value: string) => {
        if (!value.trim()) {
          return "Password is required";
        }
        if (value.length < 8) {
          return "Password must be at least 8 characters";
        }
        return null;
      },
      password2: (value: string, values: FormValues) => {
        if (!value.trim()) {
          return "Please confirm your password";
        }
        if (value !== values.password) {
          return "Passwords do not match";
        }
        return null;
      },
      branch: (value: string) => {
        if (!value) {
          return "Please select your branch";
        }
        return null;
      },
      scn: (value: string) => {
        if (!value.trim()) {
          return "Enrollment Number is required";
        }
        // Strict SCN format validation: SCN + 6 digits
        if (!/^SCN\d{6}$/.test(value.trim())) {
          return "Format must be SCN followed by 6 digits (e.g. SCN123456)";
        }
        return null;
      },
    },
  });

  async function onSubmit(values: typeof form.values) {
    setIsSubmitting(true);

    try {
      const endpoint = Endpoints.GetRegister();
      const response = await PerformRequest<SignupResponse>({
        method: endpoint.method,
        route: endpoint.route,
        data: {
          email: values.email.trim(),
          password: values.password,
          password2: values.password2,
          first_name: values.firstName.trim(),
          last_name: values.lastName.trim(),
          branch: values.branch,
          scn: values.scn.trim(),
        },
        doNotUseAuthorization: true,
      });

      if (
        (response.status === 201 || response.status === 200) &&
        response.data
      ) {
        notifications.show({
          title: "Success",
          message: "Account created successfully! Please verify your email.",
          color: "green",
        });

        // Navigate to verify email page
        setTimeout(() => {
          navigate(
            `/verify-email?email=${encodeURIComponent(values.email.trim())}`
          );
        }, 500);
      } else {
        // Handle error response
        const errorData = (response.data || {}) as SignupError;
        let errorMessage = "Registration failed. Please try again.";

        // Handle field-specific errors (API returns arrays of error messages)
        if (errorData?.email && Array.isArray(errorData.email)) {
          errorMessage = errorData.email[0];
        } else if (errorData?.password && Array.isArray(errorData.password)) {
          errorMessage = errorData.password[0];
        } else if (errorData?.password2 && Array.isArray(errorData.password2)) {
          errorMessage = errorData.password2[0];
        } else if (
          errorData?.first_name &&
          Array.isArray(errorData.first_name)
        ) {
          errorMessage = errorData.first_name[0];
        } else if (errorData?.last_name && Array.isArray(errorData.last_name)) {
          errorMessage = errorData.last_name[0];
        }

        notifications.show({
          title: "Registration Failed",
          message: errorMessage,
          color: "red",
        });
      }
    } catch (error: any) {
      let errorMessage = "An error occurred. Please try again.";

      // Handle different error response structures
      if (error?.response?.data) {
        const errorData = error.response.data as SignupError;

        // Handle field-specific errors (API returns arrays of error messages)
        if (errorData?.email && Array.isArray(errorData.email)) {
          errorMessage = errorData.email[0];
        } else if (errorData?.password && Array.isArray(errorData.password)) {
          errorMessage = errorData.password[0];
        } else if (errorData?.password2 && Array.isArray(errorData.password2)) {
          errorMessage = errorData.password2[0];
        } else if (
          errorData?.first_name &&
          Array.isArray(errorData.first_name)
        ) {
          errorMessage = errorData.first_name[0];
        } else if (errorData?.last_name && Array.isArray(errorData.last_name)) {
          errorMessage = errorData.last_name[0];
        } else if (errorData?.error) {
          errorMessage = errorData.error;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      notifications.show({
        title: "Registration Failed",
        message: errorMessage,
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthScreenShell>
      <div className="signup-container">
        <div className="auth-header">
          <img
            src={imgNbaLogo}
            alt="NBA Logo"
            className="object-contain mb-4 mx-auto nba-logo"
          />
          <h1 className="auth-title">NBA Member Registration</h1>
          <p className="auth-subtitle">
            Create your official account to manage property transactions
          </p>
        </div>

        <form
          className="auth-form space-y-4"
          onSubmit={form.onSubmit(onSubmit)}
        >
          <div className="auth-form__grid gap-3">
            <TextInput
              label="First Name"
              placeholder="Enter your first name"
              autoComplete="given-name"
              disabled={isSubmitting}
              error={form.errors.firstName}
              {...form.getInputProps("firstName")}
            />

            <TextInput
              label="Last Name"
              placeholder="Enter your last name"
              autoComplete="family-name"
              disabled={isSubmitting}
              error={form.errors.lastName}
              {...form.getInputProps("lastName")}
            />
          </div>

          <TextInput
            label="Email Address"
            placeholder="Enter your email"
            type="email"
            autoComplete="email"
            disabled={isSubmitting}
            error={form.errors.email}
            {...form.getInputProps("email")}
          />

          <PasswordInput
            label="Password"
            placeholder="Enter your password"
            autoComplete="new-password"
            disabled={isSubmitting}
            error={form.errors.password}
            {...form.getInputProps("password")}
          />

          <PasswordInput
            label="Confirm Password"
            placeholder="Confirm your password"
            autoComplete="new-password"
            disabled={isSubmitting}
            error={form.errors.password2}
            {...form.getInputProps("password2")}
          />

          {/* Enrollment Number - Emphasized */}
          <div className="form-group">
            <TextInput
              label="NBA Enrollment Number (SCN)"
              placeholder="e.g. SCN123456"
              leftSection={<Hash size={16} />}
              disabled={isSubmitting}
              error={form.errors.scn}
              description="Membership verified via NBA records - instant access for active members"
              {...form.getInputProps("scn")}
            />
          </div>

          {/* Branch Selection - New */}
          <Select
            label="NBA Branch"
            placeholder="Select your branch..."
            data={[
              "Ikeja",
              "Ikorodu",
              "Lagos Island",
              "Epe",
              "Badagry",
              "Victoria Island",
            ]}
            disabled={isSubmitting}
            error={form.errors.branch}
            {...form.getInputProps("branch")}
          />

          <button
            className="button button--primary auth-screen__submit transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nba-green)] focus-visible:ring-offset-2 flex items-center justify-center gap-2"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <ButtonLoader color="white" />
                <span>Creating account...</span>
              </>
            ) : (
              "Create Account"
            )}
          </button>

          <Link
            className="auth-screen__meta inline-flex items-center justify-center transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nba-green)] focus-visible:ring-offset-2"
            to="/login"
          >
            Already have an account, Login
          </Link>
        </form>
      </div>
    </AuthScreenShell>
  );
}
